import { formatDistanceToNow } from 'date-fns'
import { Card, CardContent } from '@/components/ui/card'
import type { IntegrationOverviewCredential } from '@/types/integrations'

interface ProviderDashboardProps {
  credentials: IntegrationOverviewCredential[]
}

export function ProviderDashboard({ credentials }: ProviderDashboardProps) {
  const activeCount = credentials.filter(c => c.is_active).length
  const totalCount = credentials.length

  // Compute aggregate stats from credentials
  const totalFailures = credentials.reduce((sum, c) => sum + c.consecutive_failures, 0)
  const hasCircuitBroken = credentials.some(c => c.health_status === 'circuit_broken')

  // Find most recent sync across all credentials
  const lastSyncTimes = credentials
    .map(c => c.last_successful_sync)
    .filter(Boolean) as string[]
  const mostRecentSync = lastSyncTimes.length > 0
    ? new Date(Math.max(...lastSyncTimes.map(t => new Date(t).getTime())))
    : null

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Active Locations</p>
            <p className="text-2xl font-bold mt-1">{activeCount} / {totalCount}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Last Sync</p>
            <p className="text-2xl font-bold mt-1">
              {mostRecentSync
                ? formatDistanceToNow(mostRecentSync, { addSuffix: true })
                : 'Never'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Active Failures</p>
            <p className={`text-2xl font-bold mt-1 ${totalFailures > 0 ? 'text-destructive' : ''}`}>
              {totalFailures}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Status</p>
            <p className={`text-2xl font-bold mt-1 ${hasCircuitBroken ? 'text-destructive' : 'text-green-600'}`}>
              {hasCircuitBroken ? 'Degraded' : activeCount > 0 ? 'Operational' : 'Inactive'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
